/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useEffect, useRef } from 'react';
import Open5eDocument, { getDocKey, getDocTitle } from "model/formats/Open5eDocument";

interface MultiSelectPopupProps<T extends string | Open5eDocument> {
  values: string;
  valuesArray?: T[];
  options: T[];
  optionClassMap?: Record<string, string>;
  singleSelection?: boolean;
  allowCustom?: boolean;
  className?: string;
  onChange: (selectedValues: T[]) => void;
  onClickOutside: (event: MouseEvent) => void;
  trailer?: React.ReactNode;
  maxSelections?: number;
  columns?: number;
  showGrouping?: boolean;
  noWrap?: boolean;
  displayFunction?: (option: T) => string | JSX.Element;
  getIdFunction?: (option: T) => string;
  filterFunction?: (option: T) => boolean;
  tooltipFunction?: (option: T) => string;
}

const MultiSelectPopup = <T extends string | Open5eDocument> ({
  values,
  valuesArray,
  options,
  optionClassMap = {},
  singleSelection = false,
  allowCustom = false,
  className = '',
  onChange,
  onClickOutside,
  trailer,
  maxSelections = Infinity,
  columns = 2,
  showGrouping = true,
  noWrap = false,
  displayFunction,
  getIdFunction,
  filterFunction,
  tooltipFunction
}: MultiSelectPopupProps<T>) => {
  const [selectedOptions, setSelectedOptions] = useState<T[]>([]);
  const [customOption, setCustomOption] = useState<string>('Other');
  const [isCustom, setIsCustom] = useState<boolean>(false);
  const popupPanelRef = useRef<HTMLSpanElement>(null);

  const getId = (item: T): string => {
    if (typeof item === 'string') {
      return item;
    }
    return getIdFunction? getIdFunction(item) : "";
  }

  const getUnionInOrderOfA = (setA: T[], setB: T[]) : T[] => {
    const setBIds = setB.map(itemB => getId(itemB));
    return setA.filter(itemA => setBIds.includes(getId(itemA)));
  }

  const getSetOfANotIncludedInB = (setA: T[], setB: T[]) : T[] => {
    const setBIds = setB.map(itemB => getId(itemB));
    return setA.filter(itemA => !setBIds.includes(getId(itemA)));
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      event.stopPropagation();
      if (popupPanelRef.current && !popupPanelRef.current.contains(event.target as Node)) {
        onClickOutside(event); // let the caller determine if this should close the popup or not
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClickOutside]);

  // Update selectedOptions when values prop changes
  useEffect(() => {
    if (valuesArray) {
      const newSelectedOptions = getUnionInOrderOfA(options, valuesArray);
      const invalidItems = getSetOfANotIncludedInB(valuesArray, options);
      if (invalidItems.length > 0 && allowCustom) {
        setIsCustom(oldVal => true);
        if (typeof invalidItems[0] === 'string') {
          setCustomOption(oldVal => invalidItems.join(', '));
        } else {
          if (invalidItems.length === 1) {
            const val = displayFunction ? displayFunction(invalidItems[0]) : invalidItems[0];
            if (typeof val === 'string') {
              setCustomOption(oldVal => val);
            } else {
              console.error('Custom options are not handled when the type of the object is not a simple string.');
            }
          } else {
            console.warn('Multiple custom items detected. Cannot combine non-string values into a single string.');
          }
        }
      } else {
        setIsCustom(oldVal => false);
      }
      setSelectedOptions(oldVal => newSelectedOptions);
    } else {
      // Step 1: Identify all options that include commas
      const stringOptions: string[] = options.filter(o => typeof o === 'string') as string[];
      const optionsWithCommas: string[] =  stringOptions.filter(o => o.includes(','));

      // Step 2: Match these comma-inclusive options against the values string
      let remainingValues: string = values; // Start with the full values string
      const matchedCommaOptions: string[] = [];
      optionsWithCommas.forEach(option => {
        if (remainingValues.includes(option)) {
          matchedCommaOptions.push(option);
          // Remove this matched option from the remaining values string
          remainingValues = remainingValues.replace(new RegExp(`\\b${option}\\b`, 'g'), '');
        }
      });

      // Step 3: Now handle the remaining values as normal options
      const valArray: string[] = remainingValues.split(/[,;]/);
      // trim all but the last entry
      for (let i=0 ; i<valArray.length ; i++) {
        if (i === valArray.length-1) {
          valArray[i] = valArray[i].trimStart();
        } else {
          valArray[i] = valArray[i].trim();
        }
      }
      // ignore any blank entries
      const remainingOptions: string[] = valArray.filter(o => o !== '');

      // Step 4: Combine matched comma options with remaining options
      const optionIds: string[] = options.map(o => getId(o));
      const allMatchedOptions = [...matchedCommaOptions, ...remainingOptions.filter(o => optionIds.includes(o))];
      const unmatchedOptions = remainingOptions.filter(o => !optionIds.includes(o));
      const matchedOptions: T[] =  options.filter(itemA => allMatchedOptions.includes(getId(itemA)));

      // Step 5: Handle custom options
      const newIsCustom = (unmatchedOptions.length > 0) && allowCustom;

      if (values === '' && matchedOptions.length === 0 && options.includes('' as T)) {
        matchedOptions.push('' as T);
      }
  
      // Update state based on these computed values
      setSelectedOptions(oldVal => matchedOptions);
      setIsCustom(oldVal => newIsCustom);
      if (newIsCustom) {
        setCustomOption(oldVal => unmatchedOptions.join(', '));
      }
    }
  }, [values, valuesArray, options, allowCustom, displayFunction]);

  const handleCustomInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomOption(oldVal => event.target.value);
    setIsCustom(oldVal => true);
    const newTval = event.target.value as unknown as T;
    if (singleSelection) {
      setSelectedOptions(oldVal => [newTval]);
      onChange([newTval]);
    } else {
      const orderedOptionsArray = getUnionInOrderOfA(options, selectedOptions);
      orderedOptionsArray.push(newTval);
      onChange(orderedOptionsArray);
    }
  };

  const handleCustomInputBlur = () => {
    const orderedOptionsArray = getUnionInOrderOfA(options, selectedOptions);
    if (isCustom && singleSelection) {
      onChange([customOption as unknown as T]);
    } else if (isCustom) {
      onChange([...orderedOptionsArray, customOption as unknown as T]);
    } else {
      onChange(orderedOptionsArray);
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, option: T) => {
    event.stopPropagation(); // Stop event propagation to prevent closing the panel
    if (option === ("custom" as unknown as T)) {
      setIsCustom(prevCustom => {
        const updatedCustomState = !prevCustom;
        let newVal: T[] = [];
        if (singleSelection) {
          setSelectedOptions(oldVal => updatedCustomState ? [customOption as unknown as T] : []);
          if (updatedCustomState) {
            newVal = [customOption as unknown as T];
          }
        } else {
          const orderedOptionsArray = getUnionInOrderOfA(options, selectedOptions);
          if (updatedCustomState) {
            orderedOptionsArray.push(customOption as unknown as T);
          }
          newVal = orderedOptionsArray;
        }
        // call the onChange in a timeout so we no longer in the render phase:
        setTimeout(() => onChange(newVal), 100);
        return updatedCustomState;
      });
    } else {
      setSelectedOptions(prevOptionsArray => {
        let newOptionsArray = prevOptionsArray.includes(option)
          ? prevOptionsArray.filter(item => item !== option)
          : [...prevOptionsArray, option];
        if (singleSelection) {
          newOptionsArray = [option];
        }
        // Set the order to match the input options set.
        let orderedOptionsArray = getUnionInOrderOfA(options, newOptionsArray);
        if (isCustom && !singleSelection) {
          orderedOptionsArray = [...orderedOptionsArray, customOption as unknown as T];
        }
        // call the onChange in a timeout so we no longer in the render phase:
        setTimeout(() => onChange(orderedOptionsArray), 100);
        return newOptionsArray;
      });
    }
  };

  const inputType = singleSelection ? "radio" : "checkbox";

  const renderOption = (option: T, index: number, colStart: number, colEnd: number): JSX.Element | null => {
    if (index < colStart || index >= colEnd) {
      return null; // Skip rendering if the index is outside of the column range
    }
    const inputId = `input_${index}${typeof option === 'string' ? option : getDocKey(option)}`;
    const isChecked = selectedOptions.map(o=>getId(o)).includes(getId(option));
    const optionClass = optionClassMap ? (optionClassMap[getId(option)] || '') : '';
    const isDisabled = !isChecked &&
                       (((maxSelections !== null) && selectedOptions.length >= maxSelections) ||
                        (filterFunction != null && !filterFunction(option)));

    let val = null;
    if (displayFunction) {
      val = displayFunction(option);
    } else if (typeof option === 'string') {
      val = option;
    }
    if (val == null) {
      console.error(`Unable to render MultiSelectPopup. Only "string" objects are allowed.`);
      return null;
    }

    let tooltip = '';
    if (tooltipFunction) {
      tooltip = tooltipFunction(option);
    }

    return (
      <li key={index} className={className} style={noWrap ? {whiteSpace: 'nowrap'} : undefined}>
        <label htmlFor={inputId}>
          <input id={inputId} type={inputType}
            checked={isChecked} name='popup'
            onChange={(event) => handleCheckboxChange(event, option)}
            disabled={isDisabled}
          />
          <span className={optionClass} title={tooltip}>{val}</span>
        </label>
      </li>
    );
  }

  const renderCustomOption = () => (
    <li key={'custom'}>
      <label>
        <input type={inputType} value="custom" checked={isCustom}
          onChange={(event) => handleCheckboxChange(event, "custom" as unknown as T)}
        />
        <input style={{ width: 'calc(100% - 30px)' }} type="text"
          value={customOption} onChange={handleCustomInputChange}
          onBlur={handleCustomInputBlur} />
      </label>
    </li>
  );

  const renderColumn = (column: number, opts: T[]) => {
    const optionsCount = opts.length + (allowCustom ? 1 : 0);
    const optionsPerColumn = optionsCount / columns;
    const colStart = Math.ceil(optionsPerColumn * column);
    const colEnd = Math.ceil(optionsPerColumn * (column + 1));
    const isLastColumn = (column === columns - 1);

    return (column < columns) && (
      <td style={{ alignContent: 'flex-start' }} key={'col'+column}>
        <ul className='multi-select-popup'>
          {opts.map((option, index) => renderOption(option, index, colStart, colEnd))}
          {allowCustom && isLastColumn && renderCustomOption()}
        </ul>
      </td>
    );
  }

  const groups = new Map<string, T[]>();
  for (const opt of options) { // opt is of type T, which is (string | Open5eDocument)
    // If this is a string, we can't put this in a group by document title, so we use the group ''.
    const key = (!showGrouping || typeof opt === 'string') ? '' : getDocTitle(opt);
    let group = groups.get(key);
    if (group == null) {
      groups.set(key, group = []);
    }
    group.push(opt);
  }
  // Everything is sorted with natural collation (so "Tome of Beasts 2" sorts before "Tome of Beasts 11", "5e ..." should be first).
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  const keys: string[] = [...groups.keys()].sort(collator.compare);

  return (
    <span className="multi-select-container edit-field" ref={popupPanelRef} >
      <span className="multi-select-panel">
        <table className={className}>
          <tbody>
            {keys.filter(k => groups.get(k)?.length).map((k, index) => (
              <React.Fragment key={index}>
                <tr><td colSpan={columns} style={{fontSize: 'x-large', fontWeight: 700, color: 'black'}}>{(index > 0) && <hr/>}{k}</td></tr>
                <tr>
                  {Array.from({ length: columns }).map((_, column) => renderColumn(column, groups.get(k)!))}
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {trailer && <span style={{ margin: '10px' }}>{trailer}<br />&nbsp;</span>}
      </span>
    </span>
  );
};

export default MultiSelectPopup;
